<template>
  <div>
    <ib-dialog
      :visible="introDialogVisible"
      :width="introDialogWidth"
      :closable="false"
      empty-header
      show-close
      @close="onSkip"
      @closed="onDialogClosed"
    >
      <template #title>
        <span />
      </template>
      <div v-if="$slots['intro-image']" class="ib-guided-tour__image text-center mb-3">
        <slot name="intro-image" />
      </div>
      <slot name="intro" />
      <template #footer>
        <slot name="intro-footer" :start-tour="onStartTour" :skip="onSkip">
          <ib-dialog-footer
            class="mb-3"
            :submit-btn-text="$t('startTour')"
            :cancel-btn-text="$t('skip')"
            button-size="md"
            center-buttons
            @submit="onStartTour"
            @cancel="onSkip"
          />
        </slot>
        <div class="v-step__dots">
          <span
            v-for="index in totalSteps"
            :key="index"
            class="v-step__dot"
            :class="{ 'v-step__dot--active': index === 1 }"
          />
        </div>
      </template>
    </ib-dialog>

    <v-tour :name="tourName" :steps="steps" :callbacks="tourCallbacks">
      <template slot-scope="tour">
        <transition name="fade">
          <v-step
            v-if="tour.steps[tour.currentStep]"
            :key="tour.currentStep"
            :step="tour.steps[tour.currentStep]"
            :previous-step="tour.previousStep"
            :next-step="tour.nextStep"
            :stop="tour.stop"
            :skip="tour.skip"
            :is-first="tour.isFirst"
            :is-last="tour.isLast"
            :labels="tour.labels"
          >
            <template #header>
              <div v-if="tour.steps[tour.currentStep].header" class="v-step__header">
                <div v-if="tour.steps[tour.currentStep].header.title" class="v-step__title">
                  {{ tour.steps[tour.currentStep].header.title }}
                </div>
              </div>
              <button
                type="button"
                aria-label="Close"
                class="v-step__close-btn"
                @click="tour.stop"
              >
                <i class="el-icon el-icon-close" />
              </button>
            </template>
            <template #actions>
              <div class="v-step__buttons">
                <ib-button
                  v-if="!tour.isLast"
                  size="md"
                  font-family="semi-bold"
                  @click="tour.nextStep"
                >
                  {{ $t('next') }}
                </ib-button>
                <ib-button
                  v-if="tour.isLast"
                  size="md"
                  font-family="semi-bold"
                  @click="tour.stop"
                >
                  {{ $t('finish') }}
                </ib-button>
              </div>
              <div class="v-step__dots">
                <span
                  v-for="index in totalSteps"
                  :key="index"
                  class="v-step__dot"
                  :class="{ 'v-step__dot--active': index === (showIntroDialog ? tour.currentStep + 2 : tour.currentStep + 1) }"
                />
              </div>
            </template>
          </v-step>
        </transition>
      </template>
    </v-tour>
  </div>
</template>

<script>
export default {
  name: 'IbGuidedTour',

  props: {
    tourName: {
      type: String,
      required: true
    },

    steps: {
      type: Array,
      required: true
    },

    showIntroDialog: {
      type: Boolean,
      default: false
    },

    autoStart: {
      type: Boolean,
      default: true
    },

    introDialogWidth: {
      type: String,
      default: '500px'
    },

    introDelay: {
      type: Number,
      default: 0
    }
  },

  data () {
    return {
      introDialogVisible: false,
      pendingStart: false,
      delayTimer: null
    }
  },

  computed: {
    totalSteps () {
      return (this.showIntroDialog ? 1 : 0) + this.steps.length
    },

    tourCallbacks () {
      return {
        onStart: this.onTourStart,
        onNextStep: this.onStepChange,
        onPreviousStep: this.onStepChange,
        onStop: this.onTourEnd
      }
    }
  },

  mounted () {
    if (this.showIntroDialog) {
      if (this.introDelay > 0) {
        this.delayTimer = setTimeout(() => {
          this.introDialogVisible = true
        }, this.introDelay)
      } else {
        this.introDialogVisible = true
      }
    } else if (this.autoStart) {
      this.$nextTick(() => {
        this.startTour()
      })
    }
  },

  beforeDestroy () {
    clearTimeout(this.delayTimer)
    document.body.classList.remove(`v-tour--${this.tourName}`)
    this.removeStepClasses()
  },

  methods: {
    start () {
      if (this.showIntroDialog) {
        if (this.introDelay > 0) {
          this.delayTimer = setTimeout(() => {
            this.introDialogVisible = true
          }, this.introDelay)
        } else {
          this.introDialogVisible = true
        }
      } else {
        this.startTour()
      }
    },

    startTour () {
      if (this.$tours[this.tourName]) {
        this.$tours[this.tourName].start()
      }
    },

    onStartTour () {
      this.pendingStart = true
      this.introDialogVisible = false
    },

    onSkip () {
      if (this.pendingStart) return
      this.pendingStart = false
      this.introDialogVisible = false
      this.$emit('intro-skip')
    },

    onDialogClosed () {
      if (this.pendingStart) {
        this.pendingStart = false
        this.$nextTick(() => {
          this.startTour()
        })
      }
    },

    onTourStart () {
      document.body.classList.add(`v-tour--${this.tourName}`)
      this.updateStepClass(0)
      this.$emit('tour-start')
    },

    onStepChange () {
      this.$nextTick(() => {
        const tour = this.$tours[this.tourName]
        if (tour) {
          this.updateStepClass(tour.currentStep)
        }
      })
    },

    updateStepClass (stepIndex) {
      this.removeStepClasses()
      document.body.classList.add(`v-tour--${this.tourName}--step-${stepIndex}`)
    },

    removeStepClasses () {
      const classList = document.body.classList
      const prefix = `v-tour--${this.tourName}--step-`
      Array.from(classList).forEach(cls => {
        if (cls.startsWith(prefix)) {
          classList.remove(cls)
        }
      })
    },

    onTourEnd () {
      document.body.classList.remove(`v-tour--${this.tourName}`)
      this.removeStepClasses()
      this.$emit('tour-end')
    }
  }
}
</script>
